import React from 'react';
import PropTypes from 'prop-types';

/**
 * Composant Button unifié pour l'application.
 * Ce composant est le composant standard pour tous les boutons de l'application.
 * 
 * @example
 * // Bouton principal (primary)
 * <Button variant="primary">Cliquez ici</Button>
 * 
 * @example
 * // Bouton avec border-radius personnalisé
 * <Button variant="primary" radius="md">Bouton avec radius moyen</Button>
 * 
 * @example
 * // Bouton de soumission de formulaire pleine largeur
 * <Button type="submit" variant="primary" full>
 *   Soumettre le formulaire
 * </Button>
 * 
 * @example
 * // Bouton avec icône
 * <Button variant="ghost" icon>
 *   <span className="icon">✏️</span>
 * </Button>
 * 
 * @example
 * // Bouton en état de chargement
 * <Button variant="primary" loading>
 *   Traitement en cours...
 * </Button>
 * 
 * @example
 * // Bouton de petite taille avec gestionnaire de clic
 * <Button 
 *   variant="secondary" 
 *   size="sm" 
 *   onClick={() => {}}
 * </Button>
 * 
 * Guide d'utilisation :
 * 1. Toujours préférer le composant Button à une balise <button> native
 * 2. Variantes disponibles :
 *    - primary : action principale, bouton le plus visible
 *    - secondary : actions secondaires
 *    - success : actions de confirmation/succès
 *    - warning : actions nécessitant attention
 *    - danger : actions destructives/dangereuses
 *    - ghost : actions subtiles, intégrées au contenu
 * 
 * 3. Tailles disponibles :
 *    - sm : petits boutons (actions compactes)
 *    - md : taille par défaut
 *    - lg : boutons plus grands (actions importantes)
 * 
 * 4. Border-radius disponibles :
 *    - sm : petit radius (coins légèrement arrondis)
 *    - md : radius moyen (par défaut)
 *    - lg : grand radius (coins très arrondis)
 * 
 * 5. Options spéciales :
 *    - full : prend toute la largeur disponible
 *    - icon : optimise le style pour les boutons avec icône
 *    - disabled : désactive le bouton
 *    - loading : affiche un indicateur de chargement et désactive les interactions
 *    - type : 'button' (défaut), 'submit' pour les formulaires, 'reset'
 *    - className : permet d'ajouter des classes CSS personnalisées
 * 
 * 5. Bonnes pratiques :
 *    - Utiliser 'primary' avec parcimonie (une seule action principale par vue)
 *    - Préférer 'secondary' pour la majorité des actions
 *    - Réserver 'danger' aux actions destructives
 *    - Utiliser 'ghost' pour les actions contextuelles/intégrées
 *    - Pour les formulaires, mettre type="submit" sur le bouton principal
 */
const Button = ({
  children,
  variant = 'primary',
  size,
  radius,
  full = false,
  icon = false,
  loading = false,
  className = '',
  disabled = false,
  type = 'button',
  onClick,
  ...props
}) => {
  const baseClass = 'wa-button';
  
  // Le bouton est considéré comme désactivé s'il est explicitement disabled ou en loading
  const isDisabled = disabled || loading;
  
  // Construction des classes CSS
  const buttonClasses = [
    baseClass,
    `${baseClass}--${variant}`,
    size ? `${baseClass}--${size}` : '',
    radius ? `${baseClass}--radius-${radius}` : '',
    full ? `${baseClass}--full` : '',
    icon ? `${baseClass}--icon` : '',
    loading ? `${baseClass}--loading` : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={isDisabled}
      onClick={loading ? undefined : onClick}
      {...props}
    >
      <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
        {children}
      </span>
    </button>
  );
};

Button.propTypes = {
  /** Contenu du bouton */
  children: PropTypes.node.isRequired,
  /** Variante de style du bouton */
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'ghost']),
  /** Taille du bouton */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Taille du border-radius */
  radius: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Si true, le bouton prendra toute la largeur disponible */
  full: PropTypes.bool,
  /** Si true, le bouton aura un style optimisé pour les icônes */
  icon: PropTypes.bool,
  /** Si true, le bouton affichera un indicateur de chargement et sera désactivé */
  loading: PropTypes.bool,
  /** Classes CSS supplémentaires */
  className: PropTypes.string,
  /** État désactivé du bouton */
  disabled: PropTypes.bool,
  /** Type HTML du bouton */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Fonction appelée lors du clic */
  onClick: PropTypes.func,
};

export default Button;
